import { useState } from "react";
import { Play } from "lucide-react";
import PageHero from "@/components/PageHero";
import VideoModal from "@/components/VideoModal";
import CTABanner from "@/components/CTABanner";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/ContentContext";

const PlayButton = ({ small = false, testId }) => (
  <span
    data-testid={testId}
    className={`relative flex items-center justify-center rounded-full border border-white/40 bg-white/15 text-white backdrop-blur-md transition-all duration-500 ease-premium group-hover:scale-110 group-hover:bg-white/25 ${
      small ? "h-14 w-14" : "h-20 w-20 md:h-24 md:w-24"
    }`}
  >
    <span aria-hidden="true" className="absolute inset-0 rounded-full border border-white/50 animate-pulse-ring" />
    <Play className={`fill-current ${small ? "ml-0.5 h-5 w-5" : "ml-1 h-7 w-7"}`} />
  </span>
);

export default function VideosPage() {
  const D = useSite();
  const p = D.pages.videolar;
  const [active, setActive] = useState(null);
  const [featured, ...rest] = D.videos;

  return (
    <>
      <PageHero overline={p.overline} titleLines={p.titleLines} description={p.description} />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12">
        {featured && (
          <Reveal>
          <button
            data-testid="featured-video-card"
            onClick={() => setActive(featured)}
            className="group relative block h-[60vh] min-h-[400px] w-full overflow-hidden rounded-[2.5rem] text-left shadow-lift"
          >
            <img
              src={featured.poster}
              alt={featured.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1600ms] ease-premium group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/75 via-charcoal-950/15 to-charcoal-950/25" />
            <div className="absolute bottom-8 left-8 md:bottom-12 md:left-14">
              <p className="font-accent text-[11px] uppercase tracking-[0.35em] text-gold-light">
                {featured.subtitle} — {featured.duration}
              </p>
              <h2 className="mt-3 font-serif text-3xl tracking-tight text-white md:text-5xl">{featured.title}</h2>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayButton testId="featured-video-play" />
            </div>
          </button>
          </Reveal>
        )}

        <div className="mt-10 grid gap-8 md:grid-cols-2">
          {rest.map((video, i) => (
            <Reveal key={video.id} delay={i * 0.1}>
              <button
                data-testid={`video-card-${video.id}`}
                onClick={() => setActive(video)}
                className="group relative block aspect-video w-full overflow-hidden rounded-[2rem] text-left shadow-premium transition-shadow duration-700 hover:shadow-lift"
              >
                <img
                  src={video.poster}
                  alt={video.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1400ms] ease-premium group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8">
                  <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-light">
                    {video.subtitle} — {video.duration}
                  </p>
                  <h3 className="mt-2 font-serif text-2xl tracking-tight text-white md:text-3xl">{video.title}</h3>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <PlayButton small testId={`video-play-${video.id}`} />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <VideoModal video={active} onClose={() => setActive(null)} />
      <CTABanner />
    </>
  );
}
